import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';

export interface ApiError {
  code: string;
  message: string;
  messageAr?: string;
  hint?: string;
  type: string;
  timestamp: string;
  requestId?: string;
  validationErrors?: Record<string, string[]>;
  errors?: Array<{
    code: string;
    description: string;
    field?: string;
  }>;
}

export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

export interface SuccessMessage {
  message: string;
  type?: 'success' | 'info' | 'warning';
  duration?: number;
}

class ErrorHandlerService {
  private t: any;
  private isRTL: boolean;
  private lastMessage: { content: string; timestamp: number } | null = null;
  private readonly MESSAGE_DEBOUNCE_MS = 2000; // Prevent duplicate messages within 2 seconds
  private activeToasts: Set<string> = new Set();

  constructor() {
    // This will be initialized when used in components
    this.t = null;
    this.isRTL = false;
  }

  initialize(t: any, isRTL: boolean) {
    this.t = t;
    this.isRTL = isRTL;
  }

  private isDuplicateMessage(message: string): boolean {
    const now = Date.now();
    if (this.lastMessage && 
        this.lastMessage.content === message && 
        (now - this.lastMessage.timestamp) < this.MESSAGE_DEBOUNCE_MS) {
      return true;
    }
    this.lastMessage = { content: message, timestamp: now };
    return false;
  }

  public getLocalizedMessage(key: string, fallback: string, params?: Record<string, any>): string {
    if (!this.t) return fallback;
    
    try {
      const message = this.t(key, params);
      return message !== key ? message : fallback;
    } catch {
      return fallback;
    }
  }

  private getLocalizedErrorDescription(errorCode: string, description: string): string | null {
    if (!this.t || !errorCode) return null;
    
    // Get localized description based on error code
    let localizedDesc: string | null = null;
    
    if (errorCode === 'DuplicateUserName' || errorCode === 'DuplicateEmail') {
      // Extract email/username from description if available
      const emailMatch = description?.match(/'([^']+)'/);
      const email = emailMatch ? emailMatch[1] : '';
      
      if (errorCode === 'DuplicateEmail') {
        localizedDesc = email 
          ? this.t('auth.duplicateEmail', { defaultValue: `Email '${email}' is already taken.`, email })
          : this.t('auth.emailAlreadyExists', 'This email is already registered.');
      } else if (errorCode === 'DuplicateUserName') {
        localizedDesc = email
          ? this.t('auth.duplicateUserName', { defaultValue: `Username '${email}' is already taken.`, email })
          : this.t('auth.usernameAlreadyExists', 'This username is already taken.');
      }
    } else if (errorCode === 'PasswordTooShort') {
      localizedDesc = this.t('auth.passwordTooShort', 'Password must be at least 6 characters long');
    } else if (errorCode === 'PasswordRequiresNonAlphanumeric') {
      localizedDesc = this.t('auth.passwordRequiresSpecial', 'Password must contain at least one special character');
    } else if (errorCode === 'PasswordRequiresDigit') {
      localizedDesc = this.t('auth.passwordRequiresDigit', 'Password must contain at least one digit');
    } else if (errorCode === 'PasswordRequiresUpper') {
      localizedDesc = this.t('auth.passwordRequiresUpper', 'Password must contain at least one uppercase letter');
    } else if (errorCode === 'PasswordRequiresLower') {
      localizedDesc = this.t('auth.passwordRequiresLower', 'Password must contain at least one lowercase letter');
    } else if (errorCode === 'UNIQUE_CONSTRAINT_VIOLATION') {
      localizedDesc = this.t('errors.uniqueConstraintViolation', 'This item already exists. Please use a different value.');
    } else if (errorCode === 'DATABASE_UPDATE_ERROR') {
      localizedDesc = this.t('errors.databaseUpdateError', 'We couldn\'t save your changes.');
    } else if (errorCode === 'UNAUTHORIZED_ACCESS') {
      localizedDesc = this.t('errors.unauthorizedAccess', 'You are not authorized to perform this action.');
    } else if (errorCode === 'MISSING_REQUIRED_FIELD') {
      localizedDesc = this.t('errors.missingRequiredField', 'Missing information. Please check all required fields.');
    } else if (errorCode === 'INVALID_ARGUMENT') {
      localizedDesc = this.t('errors.invalidArgument', 'Invalid values. Please check your input.');
    } else if (errorCode === 'FILE_NOT_FOUND') {
      localizedDesc = this.t('errors.fileNotFound', 'The requested file was not found.');
    } else if (errorCode === 'REQUEST_TIMEOUT') {
      localizedDesc = this.t('errors.requestTimeout', 'Request timeout. Please try again.');
    } else if (errorCode === 'INTERNAL_SERVER_ERROR') {
      localizedDesc = this.t('errors.internalServerError', 'An unexpected error occurred. Please try again.');
    }
    
    // If no localized description found, return null (use original description)
    return localizedDesc;
  }

  private getErrorMessage(error: any): string {
    if (!error) return this.getLocalizedMessage('errors.unknownError', 'An unknown error occurred');

    // Handle network errors
    if (!error.response) {
      return this.getLocalizedMessage('errors.networkError', 'Network connection error. Please check your internet connection.');
    }

    // Handle different HTTP status codes - CHECK 429 FIRST before processing response data
    const status = error.response?.status;
    
    // Priority: Check for rate limit errors first (429) regardless of response body format
    if (status === 429) {
      // Check for Retry-After header to provide more context
      // Axios normalizes headers to lowercase, but check both cases
      const retryAfterHeader = error.response?.headers?.['retry-after'] || error.response?.headers?.['Retry-After'];
      if (retryAfterHeader) {
        const seconds = typeof retryAfterHeader === 'string' ? parseInt(retryAfterHeader, 10) : retryAfterHeader;
        if (!isNaN(seconds) && seconds > 0) {
          return this.isRTL
            ? `طلبات كثيرة جداً. يرجى الانتظار ${seconds} ثانية قبل المحاولة مرة أخرى.`
            : `Too many requests. Please wait ${seconds} seconds before trying again.`;
        }
      }
      return this.getLocalizedMessage('errors.rateLimit', 'Too many requests. Please wait a moment and try again.');
    }

    // Handle API error response for other status codes
    if (error.response?.data) {
      const apiError = error.response.data as ApiError;
      
      // Build error message with descriptions
      let mainMessage = '';
      const errorDescriptions: string[] = [];
      
      // Get main message (localized)
      if (apiError.message) {
        mainMessage = this.isRTL && apiError.messageAr 
          ? apiError.messageAr 
          : apiError.message;
      }
      
      // Process errors array if present (for Identity errors like DuplicateEmail, etc.)
      if (apiError.errors && Array.isArray(apiError.errors) && apiError.errors.length > 0) {
        apiError.errors.forEach((err: any) => {
          // Skip field-specific errors (they're shown inline)
          if (!err.field && err.code && err.description) {
            const localizedDesc = this.getLocalizedErrorDescription(err.code, err.description);
            if (localizedDesc) {
              errorDescriptions.push(localizedDesc);
            } else {
              // If no localized version, use original description
              errorDescriptions.push(err.description);
            }
          }
        });
      }
      
      // Append descriptions to main message if available
      if (errorDescriptions.length > 0) {
        return mainMessage 
          ? `${mainMessage} ${errorDescriptions.join(' ')}`
          : errorDescriptions.join(' ');
      }
      
      // Return main message if no descriptions to append
      if (mainMessage) {
        return mainMessage;
      }
    }

    // Handle other HTTP status codes
    switch (status) {
      case 400:
        return this.getLocalizedMessage('errors.badRequest', 'Invalid request. Please check your input.');
      case 401:
        return this.getLocalizedMessage('errors.unauthorized', 'You are not authorized. Please log in again.');
      case 403:
        return this.getLocalizedMessage('errors.forbidden', 'You do not have permission to perform this action.');
      case 404:
        return this.getLocalizedMessage('errors.notFound', 'The requested resource was not found.');
      case 409:
        return this.getLocalizedMessage('errors.conflict', 'A conflict occurred. Please refresh and try again.');
      case 422:
        return this.getLocalizedMessage('errors.validationError', 'Validation failed. Please check your input.');
      case 500:
        return this.getLocalizedMessage('errors.serverError', 'Server error. Please try again later.');
      case 503:
        return this.getLocalizedMessage('errors.serviceUnavailable', 'Service temporarily unavailable. Please try again later.');
      default:
        return this.getLocalizedMessage('errors.apiError', 'An API error occurred');
    }
  }

  private getErrorHint(error: any): string | null {
    if (error?.response?.data?.hint) {
      return error.response.data.hint;
    }
    return null;
  }

  private getValidationErrors(error: any): ValidationError[] {
    const errors: ValidationError[] = [];
    
    if (error?.response?.data?.validationErrors) {
      const validationErrors = error.response.data.validationErrors;
      Object.entries(validationErrors).forEach(([field, messages]) => {
        if (Array.isArray(messages)) {
          messages.forEach(message => {
            errors.push({
              field,
              message: String(message),
              code: 'VALIDATION_ERROR'
            });
          });
        }
      });
    }

    return errors;
  }

  handleError(error: any, options?: {
    showToast?: boolean;
    showValidationErrors?: boolean;
    customMessage?: string;
    logError?: boolean;
    preventDuplicate?: boolean;
  }): {
    message: string;
    hint?: string;
    validationErrors: ValidationError[];
    code: string;
  } {
    const {
      showToast = true,
      showValidationErrors = true,
      customMessage,
      logError = true,
      preventDuplicate = true
    } = options || {};

    if (logError) {
      console.error('Error handled:', error);
    }

    const message = customMessage || this.getErrorMessage(error);
    const hint = this.getErrorHint(error);
    const validationErrors = this.getValidationErrors(error);
    const code = error?.response?.data?.code || 'UNKNOWN_ERROR';

    if (showToast) {
      // Prevent duplicate messages
      if (preventDuplicate && this.isDuplicateMessage(message)) {
        return { message, hint: hint || undefined, validationErrors, code };
      }

      // Show main error message only (one toast per error)
      toast.error(message, {
        duration: 5000,
        position: this.isRTL ? 'top-left' : 'top-right',
      });

      // Only show hint if there are no validation errors (to avoid spam)
      if (hint && (!showValidationErrors || validationErrors.length === 0)) {
        setTimeout(() => {
          if (!this.isDuplicateMessage(hint)) {
            toast.error(hint, {
              duration: 4000,
              position: this.isRTL ? 'top-left' : 'top-right',
            });
          }
        }, 100);
      }

      // Show validation errors inline, not as toasts (they're shown in form fields)
      // Only show as toast if explicitly requested and no field errors exist
      if (showValidationErrors && validationErrors.length > 0 && validationErrors.every(e => !e.field)) {
        // Only show the first validation error as toast
        const firstError = validationErrors[0];
        if (firstError.message && !this.isDuplicateMessage(firstError.message)) {
          setTimeout(() => {
            toast.error(firstError.message, {
              duration: 4000,
              position: this.isRTL ? 'top-left' : 'top-right',
            });
          }, 200);
        }
      }
    }

    return {
      message,
      hint: hint || undefined,
      validationErrors,
      code
    };
  }

  showSuccess(message: string, options?: {
    duration?: number;
    customMessage?: string;
    preventDuplicate?: boolean;
  }): void {
    const {
      duration = 3000,
      customMessage,
      preventDuplicate = true
    } = options || {};

    const displayMessage = customMessage || message;
    
    // Prevent duplicate success messages
    if (preventDuplicate && this.isDuplicateMessage(displayMessage)) {
      return;
    }
    
    toast.success(displayMessage, {
      duration,
      position: this.isRTL ? 'top-left' : 'top-right',
    });
  }

  showInfo(message: string, options?: {
    duration?: number;
    preventDuplicate?: boolean;
  }): void {
    const { 
      duration = 4000,
      preventDuplicate = true
    } = options || {};
    
    // Prevent duplicate info messages
    if (preventDuplicate && this.isDuplicateMessage(message)) {
      return;
    }
    
    toast(message, {
      duration,
      position: this.isRTL ? 'top-left' : 'top-right',
      icon: 'ℹ️',
    });
  }

  showWarning(message: string, options?: {
    duration?: number;
    preventDuplicate?: boolean;
  }): void {
    const { 
      duration = 4000,
      preventDuplicate = true
    } = options || {};
    
    // Prevent duplicate warning messages
    if (preventDuplicate && this.isDuplicateMessage(message)) {
      return;
    }
    
    toast(message, {
      duration,
      position: this.isRTL ? 'top-left' : 'top-right',
      icon: '⚠️',
    });
  }

  showLoading(message: string): string {
    return toast.loading(message, {
      position: this.isRTL ? 'top-left' : 'top-right',
    });
  }

  dismissToast(toastId: string): void {
    toast.dismiss(toastId);
  }

  dismissAll(): void {
    toast.dismiss();
  }

  // Validation helpers
  validateEmail(email: string): string | null {
    if (!email) {
      return this.getLocalizedMessage('validation.email', 'Please enter a valid email address');
    }
    
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      return this.getLocalizedMessage('validation.emailInvalid', 'Invalid email format');
    }
    
    return null;
  }

  validatePassword(password: string): string | null {
    if (!password) {
      return this.getLocalizedMessage('validation.password', 'Password must be at least 6 characters long');
    }
    
    if (password.length < 6) {
      return this.getLocalizedMessage('validation.passwordTooShort', 'Password must be at least 6 characters');
    }
    
    if (password.length > 100) {
      return this.getLocalizedMessage('validation.passwordTooLong', 'Password must be less than 100 characters');
    }
    
    return null;
  }

  validatePhone(phone: string): string | null {
    if (!phone) return null; // Phone is optional
    
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{7,20}$/;
    if (!phoneRegex.test(phone)) {
      return this.getLocalizedMessage('validation.phoneInvalid', 'Invalid phone number format');
    }
    
    return null;
  }

  validateName(name: string): string | null {
    if (!name) {
      return this.getLocalizedMessage('validation.name', 'Name must be at least 2 characters long');
    }
    
    if (name.length < 2) {
      return this.getLocalizedMessage('validation.nameTooShort', 'Name must be at least 2 characters');
    }
    
    if (name.length > 50) {
      return this.getLocalizedMessage('validation.nameTooLong', 'Name must be less than 50 characters');
    }
    
    const nameRegex = /^[a-zA-Z\u0600-\u06FF\s]{2,50}$/;
    if (!nameRegex.test(name)) {
      return this.getLocalizedMessage('validation.nameInvalid', 'Name contains invalid characters');
    }
    
    return null;
  }

  validateRequired(value: string, fieldName: string): string | null {
    if (!value || value.trim().length === 0) {
      return this.getLocalizedMessage('validation.required', 'This field is required');
    }
    return null;
  }

  validateLength(value: string, minLength: number, maxLength: number, fieldName: string): string | null {
    if (!value) {
      return this.getLocalizedMessage('validation.required', 'This field is required');
    }
    
    if (value.length < minLength) {
      return this.getLocalizedMessage('validation.minLength', `Must be at least ${minLength} characters`, { min: minLength });
    }
    
    if (value.length > maxLength) {
      return this.getLocalizedMessage('validation.maxLength', `Must be less than ${maxLength} characters`, { max: maxLength });
    }
    
    return null;
  }

  validatePrice(price: string): string | null {
    if (!price) {
      return this.getLocalizedMessage('validation.priceRequired', 'Price is required');
    }
    
    const numPrice = parseFloat(price);
    if (isNaN(numPrice)) {
      return this.getLocalizedMessage('validation.price', 'Please enter a valid price');
    }
    
    if (numPrice < 0) {
      return this.getLocalizedMessage('validation.priceInvalid', 'Price must be a positive number');
    }
    
    return null;
  }

  validatePasswordMatch(password: string, confirmPassword: string): string | null {
    if (password !== confirmPassword) {
      return this.getLocalizedMessage('validation.passwordMismatch', 'Passwords do not match');
    }
    return null;
  }
}

// Create singleton instance
export const errorHandler = new ErrorHandlerService();

// Hook for using error handler in components
export const useErrorHandler = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  
  // Initialize error handler with current translation and language context
  errorHandler.initialize(t, isRTL);
  
  return errorHandler;
};

export default errorHandler;
